import CheckmarkOutline16 from '@carbon/icons-react/es/checkmark--outline/16';
import Copy16 from '@carbon/icons-react/es/copy/16';
import Information16 from '@carbon/icons-react/es/information/16';
import { Row as AntRow, Col, Tooltip, Typography } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import useSound from 'use-sound';
import { EmailConfig } from 'api/account';
import { Button as StyledButton } from 'components/atoms/button';
import { Table as OzoneTable } from 'components/molecules/table';
import { Text03 } from 'components/styles';

const { Text } = Typography;

const ColumnHeaderWrapper = styled.p`
  display: inline-flex;
  margin-bottom: 0;
  align-items: center;

  .icon {
    margin-left: var(--spacing-03);
    ${Text03};
  }
`;

const CopyIcon = styled(Copy16)`
  color: var(--black-100);
`;

const Button = styled(StyledButton)`
  padding: 0px;
  height: 20px;
  background: transparent;
`;

const CopySuccessIcon = styled(CheckmarkOutline16)`
  color: var(--success-color);
`;

const Row = styled(AntRow)`
  .ant-col:nth-child(2) {
    display: none;
  }
  &:hover .ant-col:nth-child(2) {
    display: flex;
  }
`;

const getColumns = (playSound) => [
  {
    key: 'type',
    title: <FormattedMessage id="type" />,
    render() {
      return <FormattedMessage id="CNAME" />;
    },
  },
  {
    title: <FormattedMessage id="status" />,
    dataIndex: 'verified',
    render(verified) {
      return String(verified);
    },
  },
  {
    title: (
      <Tooltip
        title={
          <FormattedMessage id="settings.email_configuration.host.tooltip" />
        }
      >
        <ColumnHeaderWrapper>
          <FormattedMessage id="host_value" />
          <Information16 className="icon" />
        </ColumnHeaderWrapper>
      </Tooltip>
    ),
    dataIndex: 'host',
    width: 350,
    render(host) {
      return (
        <Row align="middle" justify="space-between">
          <Col>{host}</Col>
          <Col>
            <Button type="text" size="small" onClick={() => playSound()}>
              <Text
                copyable={{
                  text: host,
                  icon: [<CopyIcon />, <CopySuccessIcon />],
                  tooltips: [
                    <FormattedMessage id="click_to_copy" />,
                    <FormattedMessage id="copied" />,
                  ],
                }}
              />
            </Button>
          </Col>
        </Row>
      );
    },
  },
  {
    title: (
      <Tooltip
        title={
          <FormattedMessage id="settings.email_configuration.data.tooltip" />
        }
      >
        <ColumnHeaderWrapper>
          <FormattedMessage id="data" />
          <Information16 className="icon" />
        </ColumnHeaderWrapper>
      </Tooltip>
    ),
    dataIndex: 'data',
    width: 350,
    render(data) {
      return (
        <Row align="middle" justify="space-between">
          <Col>{data}</Col>
          <Col>
            <Button type="text" size="small" onClick={() => playSound()}>
              <Text
                copyable={{
                  text: data,
                  icon: [<CopyIcon />, <CopySuccessIcon />],
                  tooltips: [
                    <FormattedMessage id="click_to_copy" />,
                    <FormattedMessage id="copied" />,
                  ],
                }}
              />
            </Button>
          </Col>
        </Row>
      );
    },
  },
];

export const Table: FC<Pick<EmailConfig, 'data'>> = ({ data = [] }) => {
  const [playSound] = useSound('/sounds/dot-success.ogg');

  return (
    <OzoneTable
      columns={getColumns(playSound)}
      dataSource={data}
      pagination={false}
    />
  );
};
